import React from 'react';

import { Grid, GridItem } from '@chakra-ui/react';
import { Flex } from '@joinrepublic/dls-ui-kit';
import CN from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';

import { Box, Button, Link, Text } from '@r-client/shared/ui/core';
import { Location, SvgIconLight } from '@r-client/shared/ui/icons';
import { ClientRender, SafeHTML } from '@r-client/shared/util/components';

import { TGetEventsEventDetails } from '../../../types';
import { getEventFullAddress, humanizeCategory } from '../../../utils';
import { MenuButton as AddToCalendarMenuButton } from '../../add-to-calendar/menu-button/menu-button';
import {
  CalendarDate,
  Day,
  Month,
  Year,
} from '../../calendar-date/calendar-date';
import { EventCategoryIcon } from '../../event-category-icon/event-category-icon';
import { EventProperty } from '../../event-property/event-property';
import { ShareButton } from '../../share-button/share-button';
import { WeekDay } from '../../week-day/week-day';
import { EventTime } from './event-time/event-time';

import styles from './header.module.scss';

interface IHeaderProps {
  event: NonNullable<TGetEventsEventDetails>;
  onModalOpen: (id: string) => void;
  onWindowOpen: (url: string) => void;
  dataTestIds?: {
    calendarDate: string;
    eventHours: string;
    addToCalendarMenu: string;
  };
}

const getMapsUrl = (url: string) => `https://google.com/maps/search/${url}`;

export const Header: React.FC<React.PropsWithChildren<IHeaderProps>> = ({
  event,
  onModalOpen,
  onWindowOpen,
  dataTestIds,
}) => {
  const handleCtaButtonClick = () => {
    if (!event.cta) return;

    if (event.cta.rsvp) {
      onModalOpen(event.id);
      return;
    }

    if (event.cta.url) {
      onWindowOpen(event.cta.url);
    }
  };

  const handleDetailsButtonClick = () => {
    if (!event.url) return;
    onWindowOpen(event.url);
  };

  const year = formatInTimeZone(event.beginAt, event.timezone, 'yyyy');

  const day = formatInTimeZone(event.beginAt, event.timezone, 'd');

  const month = formatInTimeZone(event.beginAt, event.timezone, 'MMMM');

  const showDetailsButton = Boolean(event.url && event.useRsvp);
  const fullAddressForDisplay =
    event.address && getEventFullAddress(event.address);

  return (
    <header>
      <Grid templateColumns="repeat(24, 1fr)" gap={4}>
        <GridItem colSpan={{ base: 4, sm: 3 }}>
          <CalendarDate
            announced={!event.undisclosed}
            inactive={!!event.past}
            dataTestId={dataTestIds?.calendarDate}
          >
            <Month variant="adaptive">{month}</Month>
            <Box p="10px 0 0 0">
              <Day>{day}</Day>
            </Box>
            <Year>{year}</Year>
          </CalendarDate>
        </GridItem>

        <GridItem colSpan={{ base: 20, sm: 21 }}>
          <Flex m="0 0 0.5brm 0" alignItems="center">
            {!!event.category && (
              <EventProperty
                className={CN(styles.eventDetails, styles.category)}
              >
                <EventCategoryIcon
                  category={event.category}
                  iconClassName={styles.propertyIcon}
                />
                <Text as="span" color="gray80" type="bodyBig">
                  {humanizeCategory(event.category)}
                </Text>
              </EventProperty>
            )}

            <ClientRender>
              {!!event.beginAt && (
                <EventProperty>
                  <WeekDay
                    date={event.beginAt}
                    today="Today"
                    tomorrow="Tomorrow"
                    className={styles.eventDetails}
                  />
                </EventProperty>
              )}
            </ClientRender>

            {!event.undisclosed && (
              <EventTime event={event} dataTestId={dataTestIds?.eventHours} />
            )}
          </Flex>

          <Box m="0 0 1.5brm 0">
            {/* TODO https://linear.app/republic/issue/RCE-1324/update-eventseventtitle-output
                remove SafeHTML after fixind the issue
            */}
            <SafeHTML sanitizedHtml={event.title} className={styles.eventTitle}>
              <Text type="h2" as="h2" />
            </SafeHTML>
          </Box>

          {fullAddressForDisplay && (
            <Box m="0 0 2brm 0">
              <Flex>
                <Text type="bodyBig">
                  <SvgIconLight
                    icon={Location}
                    className={styles.addressIcon}
                  />
                </Text>
                <Link
                  href={getMapsUrl(fullAddressForDisplay)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Text type="bodyBig" weight={{ xs: '300' }}>
                    {fullAddressForDisplay}
                  </Text>
                </Link>
              </Flex>
            </Box>
          )}
        </GridItem>

        <GridItem colSpan={{ base: 24, sm: 21 }} colStart={{ sm: 4 }}>
          <Flex>
            {event.cta && (
              <>
                <Button
                  className={`${styles.ctaButton} ${styles.headerButton}`}
                  type="primary"
                  onClick={handleCtaButtonClick}
                >
                  {event.cta.text}
                </Button>
                {showDetailsButton && (
                  <Button
                    className={`${styles.ctaButton} ${styles.headerButton}`}
                    type="default"
                    onClick={handleDetailsButtonClick}
                  >
                    More details
                  </Button>
                )}
              </>
            )}
            {event.future && (
              <>
                <AddToCalendarMenuButton
                  event={event}
                  className={`${styles.headerButton} ${styles.widthFitContent}`}
                  data-testid={dataTestIds?.addToCalendarMenu}
                />
                <ShareButton
                  event={event}
                  className={`${styles.headerButton} ${styles.widthFitContent}`}
                />
              </>
            )}
          </Flex>
        </GridItem>
      </Grid>
    </header>
  );
};
